import { useWeb3React } from '@web3-react/core';
import { DeFiConnectProvider } from '@deficonnect/provider';
import { NetworkConfig } from '@deficonnect/types';
import { OfflineSigner } from '@deficonnect/cosmos-signer';
import Web3 from 'web3';
import { Eth } from 'web3-eth';
import {
  createContext,
  ReactElement,
  ReactNode,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { getKeplr, connect as connectKeplr } from '@lib/keplr-wallet';
import {
  Wallet,
  WalletType,
  CroChain,
  EthChainId,
  Network,
  CosmosHubChain,
  CosmosChain,
  JunoChain,
  TerraChain,
  AkashChain,
  CantoChain,
  IrisChain,
  CelestiaChain,
  TxChain,
} from '@types';
import { getChainInfo as getCronosChainInfo } from '@lib/cronos-chain';
import useLocalStorage from '@hooks/useLocalStorage';
import { numberToHex, promiseEach } from '@lib/utils';
import { getChainInfo as getCroChainInfo } from '@lib/cosmos-chain';
import staticConfig from '@config/static-config';
import {
  BrowserExtensionNotInstalledError,
  UserRejectedWalletConnectionRequestError,
  InvalidInputError,
  UnsupportedError,
} from '@errors';
import useScreen, { Screen } from '@hooks/useScreen';
import { InjectedConnector } from '@web3-react/injected-connector';
import useNetworkEnv from '@hooks/useNetworkEnv';
import { notify } from '@lib/bugsnag';
import { NotifiableError } from '@bugsnag/core';
import { DefiConnectOfflineSignerWrapper } from '../lib/defi-connect-signer-wrapper';

const { defiWallet, ethereumSupportedChainIds } = staticConfig;

type WindowWithEthereum = Window &
  typeof globalThis & {
    ethereum: any;
  };

interface PersistedWalletsState {
  wallets: { type: WalletType; network: Network; chain: TxChain }[];
}

export interface IWalletsContext {
  wallets: Wallet[];
  connect: (walletType: WalletType, network: Network) => Promise<void>;
  disconnect: (wallet: Wallet) => void;
  flipWallets: () => void;
  isWalletConnected: (walletType: WalletType) => boolean;
}

export const WalletsContext = createContext({} as IWalletsContext);

const getWeb3Connector = (network: Network, isTestnet: boolean) => {
  switch (network) {
    case Network.Cronos:
      return new InjectedConnector({
        supportedChainIds: isTestnet
          ? ethereumSupportedChainIds.testnet.cronos
          : ethereumSupportedChainIds.mainnet.cronos,
      });
    case Network.Ethereum:
      return new InjectedConnector({
        supportedChainIds: isTestnet
          ? ethereumSupportedChainIds.testnet.ethereum
          : ethereumSupportedChainIds.mainnet.ethereum,
      });
    default:
      throw new UnsupportedError(network);
  }
};

const getEthSupportedChainIds = (network: Network, isTestnet: boolean) => {
  switch (network) {
    case Network.Cronos:
      return isTestnet
        ? ethereumSupportedChainIds.testnet.defiWalletCronos
        : ethereumSupportedChainIds.mainnet.defiWalletCronos;
    case Network.Ethereum:
      return isTestnet
        ? ethereumSupportedChainIds.testnet.defiWalletEth
        : ethereumSupportedChainIds.mainnet.defiWalletEth;
  }
  return [];
};

const isDeFiWalletInCorrectNetwork = (
  network: Network,
  chain: CosmosChain | EthChainId,
  isTestnet: boolean
) => {
  switch (network) {
    case Network.Cronos:
    case Network.Ethereum:
      return chain === getEthSupportedChainIds(network, isTestnet)[0];
    case Network.CryptoOrg:
      return chain === (isTestnet ? CroChain.testnet4 : CroChain.mainnet);
    case Network.Cosmos:
      return (
        chain === (isTestnet ? CosmosHubChain.testnet : CosmosHubChain.mainnet)
      );
    case Network.Terra:
      return chain === (isTestnet ? TerraChain.testnet : TerraChain.mainnet);
    case Network.Juno:
      return chain === (isTestnet ? JunoChain.testnet : JunoChain.mainnet);
    case Network.Akash:
      return chain === (isTestnet ? AkashChain.testnet : AkashChain.mainnet);
    case Network.Canto:
      return chain === (isTestnet ? CantoChain.testnet : CantoChain.mainnet);
    case Network.Iris:
      return chain === (isTestnet ? IrisChain.testnet : IrisChain.mainnet);
    case Network.Celestia:
      return (
        chain === (isTestnet ? CelestiaChain.testnet : CelestiaChain.mainnet)
      );
    default:
      throw new UnsupportedError(network);
  }
};

const WalletsProvider = ({
  children,
}: {
  children: ReactNode;
}): ReactElement => {
  const [isTestnet, , hasChangedIsTestnet] = useNetworkEnv();
  const hasLoadedFromLocalStorage = useRef(false);
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [persistedWalletsStateJson, setPersistedWalletsStateJson] =
    useLocalStorage(
      'wallets-state',
      JSON.stringify({
        wallets: [],
      } as PersistedWalletsState)
    );
  const { screen } = useScreen();

  const web3 = useWeb3React();

  const disconnect = useCallback(
    (wallet: Wallet) => {
      const { type: walletType, network } = wallet;
      setWallets((prev) => {
        // Disconnect all wallets when disconnecting first one
        if (prev?.[0]?.type === walletType) {
          web3.deactivate();
          wallet.defiProvider?.close();
          return [];
        }
        switch (walletType) {
          case WalletType.metamask:
            web3.deactivate();
            break;
          case WalletType.deficonnect:
            if (network === Network.CryptoOrg || network === Network.Cosmos) {
              wallet.defiProvider?.close();
            } else {
              web3.deactivate();
            }
            break;
          case WalletType.keplr:
          default:
            break;
        }
        return prev.filter(({ type }) => type !== walletType);
      });
    },
    [setWallets, web3]
  );

  const switchToCronosChain = useCallback(async () => {
    const win = window as WindowWithEthereum;
    if (typeof win === 'undefined' || !win.ethereum) {
      throw new BrowserExtensionNotInstalledError(WalletType.metamask);
    }
    const cronosChainInfo = getCronosChainInfo(
      isTestnet ? EthChainId.CronosGravityTestnet : EthChainId.Cronos
    );
    try {
      await win.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: cronosChainInfo.chainId }],
      });
    } catch (switchError: any) {
      if (switchError.code === 4001) {
        throw new UserRejectedWalletConnectionRequestError();
      }

      try {
        await win.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [cronosChainInfo],
        });
      } catch (addError: any) {
        if (addError.code === 4001) {
          throw new UserRejectedWalletConnectionRequestError();
        }

        throw addError;
      }
    }
  }, [isTestnet]);

  const switchToEthereumChain = useCallback(async () => {
    const win = window as WindowWithEthereum;
    if (typeof win === 'undefined' || !win.ethereum) {
      throw new BrowserExtensionNotInstalledError(WalletType.metamask);
    }

    try {
      await win.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [
          {
            chainId: isTestnet
              ? numberToHex(ethereumSupportedChainIds.testnet.ethereum[0])
              : numberToHex(ethereumSupportedChainIds.mainnet.ethereum[0]),
          },
        ],
      });
    } catch (error: any) {
      if (error.code === 4001) {
        throw new UserRejectedWalletConnectionRequestError();
      }

      throw error;
    }
  }, [isTestnet]);

  const connectMetamask = useCallback(
    async (network: Network) => {
      if (web3.active) web3.deactivate();
      if (network === Network.Ethereum) {
        await switchToEthereumChain();
      } else if (network === Network.Cronos) {
        await switchToCronosChain();
      } else {
        throw new InvalidInputError(JSON.stringify(network));
      }

      await web3.activate(getWeb3Connector(network, isTestnet));

      setWallets((prev) =>
        prev.concat([
          {
            type: WalletType.metamask,
            network,
            address: web3.account || '',
            eth: web3.library?.eth as Eth | undefined,
            chain: web3.chainId as EthChainId,
            active: web3.active,
          },
        ])
      );
    },
    [isTestnet, switchToCronosChain, switchToEthereumChain, web3]
  );

  const connectKeplrWallet = useCallback(
    async (network) => {
      let chain: CosmosChain;
      switch (network) {
        case Network.CryptoOrg:
          chain = isTestnet ? CroChain.testnet4 : CroChain.mainnet;
          break;
        case Network.Cosmos:
          chain = isTestnet ? CosmosHubChain.testnet : CosmosHubChain.mainnet;
          break;
        case Network.Terra:
          chain = isTestnet ? TerraChain.testnet : TerraChain.mainnet;
          break;
        case Network.Juno:
          chain = isTestnet ? JunoChain.testnet : JunoChain.mainnet;
          break;
        case Network.Akash:
          chain = isTestnet ? AkashChain.testnet : AkashChain.mainnet;
          break;
        case Network.Canto:
          chain = isTestnet ? CantoChain.testnet : CantoChain.mainnet;
          break;
        case Network.Iris:
          chain = isTestnet ? IrisChain.testnet : IrisChain.mainnet;
          break;
        case Network.Celestia:
          chain = isTestnet ? CelestiaChain.testnet : CelestiaChain.mainnet;
          break;
        default:
          throw new InvalidInputError(JSON.stringify(network));
      }
      const keplr = await getKeplr();
      const signer = await connectKeplr(keplr, chain);
      const address = (await signer.getAccounts())[0].address;

      setWallets((prev) =>
        prev.concat([
          {
            type: WalletType.keplr,
            network,
            address,
            signer,
            chain,
            active: true,
          },
        ])
      );
    },
    [setWallets, isTestnet]
  );

  const updateDeFiConnectWallet = useCallback(
    (provider: DeFiConnectProvider, network: Network) => {
      if (!provider) throw new InvalidInputError(JSON.stringify(network));
      const { chainType, chainId, accounts } = provider;
      let eth: Eth | undefined;
      const web3 = new Web3(provider as any);
      eth = web3.eth;

      let chain: CroChain | EthChainId = 1;
      if (chainType && chainId) {
        if (chainType == 'eth') {
          chain = parseInt(chainId);
        } else {
          chain = chainId as CroChain;
        }
      }
      const address = accounts[0] ?? '';
      const defiConnectOriginalSigner = new OfflineSigner(provider);
      const wrappedSigner = new DefiConnectOfflineSignerWrapper(
        defiConnectOriginalSigner
      );

      setWallets((prev) => {
        const newWallet = {
          type: WalletType.deficonnect,
          network,
          address,
          eth,
          signer: wrappedSigner,
          chain,
          active: isDeFiWalletInCorrectNetwork(network, chain, isTestnet),
          defiProvider: provider,
        };
        if (prev.some(({ type }) => type === WalletType.deficonnect)) {
          return prev.map((wallet) => {
            if (wallet.type !== WalletType.deficonnect) {
              return wallet;
            }

            return newWallet;
          });
        }

        return prev.concat([newWallet]);
      });
    },
    [setWallets, isTestnet]
  );

  const connectDeFiConnect = useCallback(
    async (network: Network) => {
      let defiProvider: DeFiConnectProvider;
      if (network === Network.Ethereum || network === Network.Cronos) {
        const config: NetworkConfig = {
          appName: 'Cronos Token Bridge',
          chainType: 'eth',
          chainId: getEthSupportedChainIds(network, isTestnet)[0].toString(),
          rpcUrls: defiWallet.networkUrls,
        };
        defiProvider = new DeFiConnectProvider(config);
      } else if (network === Network.CryptoOrg) {
        const croChain = isTestnet ? CroChain.testnet4 : CroChain.mainnet;
        const chainInfo = getCroChainInfo(croChain);
        const config: NetworkConfig = {
          appName: 'Cronos Token Bridge',
          chainType: 'cosmos',
          chainId: isTestnet ? CroChain.testnet4 : CroChain.mainnet,
          rpcUrls: defiWallet.networkUrls,
        };
        defiProvider = new DeFiConnectProvider(config);
      } else if (network === Network.Cosmos) {
        const cosmosChain = isTestnet
          ? CosmosHubChain.testnet
          : CosmosHubChain.mainnet;
        const chainInfo = getCroChainInfo(cosmosChain);
        const config: NetworkConfig = {
          appName: 'Cronos Token Bridge',
          chainType: 'cosmos',
          chainId: chainInfo.chainId,
          rpcUrls: defiWallet.networkUrls,
        };
        defiProvider = new DeFiConnectProvider(config);
      } else {
        throw new UnsupportedError(network);
      }
      defiProvider.on('chainChanged', () => {
        if (!defiProvider.connected) {
          return;
        }
        updateDeFiConnectWallet(defiProvider, network);
      });
      defiProvider.on('accountsChanged', () => {
        if (!defiProvider.connected) {
          return;
        }
        updateDeFiConnectWallet(defiProvider, network);
      });
      defiProvider.on('disconnect', () => {
        setWallets((prev) =>
          prev.filter(({ type }) => type !== WalletType.deficonnect)
        );
      });
      await defiProvider.connect();
      updateDeFiConnectWallet(defiProvider, network);
    },
    [updateDeFiConnectWallet, isTestnet]
  );

  const isWalletConnected = useCallback(
    (walletType: WalletType) => wallets.some(({ type }) => type === walletType),
    [wallets]
  );

  const connect = useCallback(
    async (walletType: WalletType, network: Network) => {
      if (isWalletConnected(walletType)) return;
      switch (walletType) {
        case WalletType.metamask:
          return await connectMetamask(network);
        case WalletType.deficonnect:
          return await connectDeFiConnect(network);
        case WalletType.keplr:
        default:
          return await connectKeplrWallet(network);
      }
    },
    [connectKeplrWallet, connectMetamask, isWalletConnected, connectDeFiConnect]
  );

  const flipWallets = useCallback(() => {
    setWallets((prev) => [...prev.reverse()]);
  }, []);

  useEffect(() => {
    setWallets((prev) => {
      if (prev.some(({ type }) => type === WalletType.metamask)) {
        return prev.map((wallet) => {
          if (wallet.type === WalletType.metamask) {
            return {
              ...wallet,
              address: web3.account || '',
              eth: web3.library?.eth as Eth | undefined,
              chain: web3.chainId as EthChainId,
              active: web3.active,
            };
          }

          return wallet;
        });
      }

      return prev;
    });
  }, [setWallets, web3.account, web3.library?.eth, web3.chainId, web3.active]);

  useEffect(() => {
    if (hasLoadedFromLocalStorage.current) return;
    if (persistedWalletsStateJson === null) return;
    const persistedWalletsState = JSON.parse(
      persistedWalletsStateJson
    ) as PersistedWalletsState;
    const { wallets } = persistedWalletsState;
    promiseEach(wallets || [], async ({ type, network, chain }) => {
      try {
        await connect(type, network);
      } catch (error) {
        notify(error as NotifiableError);
      }
    });
    hasLoadedFromLocalStorage.current = true;
  }, [connect, persistedWalletsStateJson]);

  useEffect(() => {
    if (!hasLoadedFromLocalStorage.current) return;
    setPersistedWalletsStateJson(
      JSON.stringify({
        wallets: wallets.map(({ type, network }) => ({ type, network })),
      } as PersistedWalletsState)
    );
  }, [setPersistedWalletsStateJson, wallets]);

  // Disconnect second wallet when not desktop screen size because mobile & tablet layout do not support second wallet
  useEffect(() => {
    const [, secondWallet] = wallets;
    if (screen && screen < Screen.desktop && secondWallet) {
      disconnect(secondWallet);
    }
  }, [screen, disconnect, wallets]);

  // Disconnect all wallets when network env is changed
  useEffect(() => {
    if (hasChangedIsTestnet) {
      const [firstWallet] = wallets;
      if (firstWallet) disconnect(firstWallet);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTestnet, hasChangedIsTestnet]);

  // Keplr account change listener
  // TODO: need to figure out how to test it
  useEffect(() => {
    const accountChangedHandler = async () => {
      if (!isWalletConnected(WalletType.keplr)) return;
      const chain = isTestnet ? CroChain.testnet4 : CroChain.mainnet;
      try {
        const keplr = await getKeplr();
        const signer = await connectKeplr(keplr, chain);
        const address = (await signer.getAccounts())[0].address;
        setWallets((prev) =>
          prev.map((wallet) => {
            if (wallet.type !== WalletType.keplr) return wallet;
            return {
              ...wallet,
              address,
            };
          })
        );
      } catch (error) {
        notify(error as NotifiableError);
      }
    };

    window.addEventListener('keplr_keystorechange', accountChangedHandler);
    return () =>
      window.removeEventListener('keplr_keystorechange', accountChangedHandler);
  }, [isTestnet, isWalletConnected]);

  return (
    <WalletsContext.Provider
      value={{
        wallets,
        flipWallets,
        connect,
        disconnect,
        isWalletConnected,
      }}
    >
      {children}
    </WalletsContext.Provider>
  );
};

export default WalletsProvider;
